import { Award, Medal, Trophy, Star, Bookmark } from "lucide-react";
import React, { useEffect, useState } from "react";
import Pagination from "../ui/Pagination";
import { Link } from "react-router-dom";
import { pluralize } from "../../helpers/general";
import UsersIcon from "../icons/UsersIcon";

interface UsersData {
  id: string;
  fullName: string;
  profilePicture: string | null;
  bookingsCount: string;
  ridesCount: string;
  totalPaid: number;
  rating: string;
}

interface AllUsersListProps {
  users: UsersData[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  totalitems: number;
  search?: string;
  onSearchChange?: (search: string) => void;
}

const AllUsersList: React.FC<AllUsersListProps> = ({
  users,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalitems,
  search,
  onSearchChange,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <>
      <div className="d-flex justify-content-between mb-4">
        <div
          className="position-relative d-flex  gap-2 w-100"
          style={{ minWidth: "250px" }}
        >
          {/* <Search
            size={22}
            className="position-absolute text-muted z-3"
            style={{ left: "12px", top: "50%", transform: "translateY(-50%)" }}
          /> */}
          {/* <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value!)}
            className="form-control ps- py-0 px-3 rounded-3 shadow-sm w-25"
            placeholder="Rechercher un chauffeur..."
          /> */}
        </div>

        <div
          className="d-flex align-items-end justify-content-center gap-3 bg-gradient-primary p-3  rounded-3"
          style={{ width: "20%" }}
        >
          <UsersIcon className="fill-white my-auto" />
          <div className="fs-5 fw-bold m-0  text-white text-pre ">
            {totalitems} {pluralize("Utilisateur", totalitems)}
          </div>
        </div>
      </div>
      {users.length > 0 ? (
        users.map((user, index) => {
          const globalIndex = (currentPage - 1) * itemsPerPage + index;
          const getBorderColor = () => {
            if (globalIndex === 0) return "#427A43";
            if (globalIndex === 1) return "#C0B87A";
            if (globalIndex === 2) return "#F1C242";
            return "#dee2e6";
          };

          const getRankIcon = () => {
            if (globalIndex === 0)
              return <Trophy style={{ color: "#427A43" }} />;
            if (globalIndex === 1)
              return <Award style={{ color: "#C0B87A" }} />;
            if (globalIndex === 2)
              return <Medal style={{ color: "#F1C242" }} />;
            return null;
          };

          return (
            <div
              key={user.id}
              className={`position-relative w-100 rounded-4 ${isMobile ? "p-2" : "p-4"} bg-white d-flex align-items-center shadow-sm justify-between mb-3`}
              style={{ border: `2px solid ${getBorderColor()}` }}
            >
              {/* Rank Icon */}
              <div
                className="position-absolute"
                style={{
                  top: "-10px",
                  right: "-10px",
                  background: "#fff",
                  borderRadius: "50%",
                  padding: "4px",
                  zIndex: 2,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                {getRankIcon()}
              </div>
              <div className="d-flex align-items-start gap-3 w-100">
                {/* Avatar */}

                {/* Info */}
                <div>
                  <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle overflow-hidden border border-secondary bg-light flex-shrink-0"
                      style={{
                        width: isMobile ? 40 : 50,
                        height: isMobile ? 40 : 50,
                      }}
                    >
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.fullName}
                          className="w-100 h-100 object-fit-cover"
                          style={{ borderRadius: "50%" }}
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary fw-bold fs-5 text-uppercase">
                          {user.fullName?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <Link
                      to={`/users/${user?.id}`}
                      className="fw-bold m-0 text-capitalize"
                    >
                      {user?.fullName}
                    </Link>
                  </div>

                  <div
                    className={`d-flex mt-2 ${isMobile ? "gap-1" : "gap-3 ms-5"}`}
                  >
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <img
                        src="/course.svg"
                        alt="courses"
                        width={isMobile ? 16 : 20}
                        height={isMobile ? 16 : 20}
                      />
                      <span style={{ fontSize: isMobile ? "12px" : "16px" }}>
                        {Number(user.ridesCount).toLocaleString()} Courses
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <img
                        src="/money.svg"
                        alt="revenue"
                        width={isMobile ? 16 : 20}
                        height={isMobile ? 16 : 20}
                      />
                      <span style={{ fontSize: isMobile ? "12px" : "16px" }}>
                        {Number(user.totalPaid).toLocaleString()} MAD
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Bookmark fill="#f97722" strokeWidth={0} />
                      <span style={{ fontSize: isMobile ? "12px" : "16px" }}>
                        {Number(user.bookingsCount).toLocaleString()}{" "}
                        Réservations
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Star fill="#f97722" strokeWidth={0} />
                      <span style={{ fontSize: isMobile ? "12px" : "16px" }}>
                        {user.rating}/5.0 étoiles
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="d-flex justify-content-end">{getRankIcon()}</div> */}
            </div>
          );
        })
      ) : (
        <div className="d-flex justify-content-center align-items-center">
          <div className="text-muted">Aucun chauffeur trouvé</div>
        </div>
      )}

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalCount={totalPages}
          pageSize={itemsPerPage}
          siblingCount={1}
        />
      </div>
    </>
  );
};

export default AllUsersList;
